import type { PersonLinksView } from '@/lib/api/generated/model';

import { coverageBucket } from './commitCoverage';

/**
 * Since your last visit (spec 164 US3): who pushes as themselves and whose
 * link broke, as this admin last saw it, in this browser. A convenience, like
 * the Overview's (spec 155): without storage every visit is a first one, and
 * nothing fails.
 */

export interface CoverageSnapshot {
  /** When the page was left. */
  at: string;
  /** Who pushed as themselves then. */
  themselves: readonly string[];
  /** Whose link needed relinking then. */
  relink: readonly string[];
}

/** Gone at least this long before the line says anything, as on the Overview. */
export const AWAY_MS = 15 * 60_000;

function keyOf(userId: string): string {
  return `commit-identity.visit.${userId}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function ids(value: unknown): string[] | null {
  return Array.isArray(value) && value.every((id) => typeof id === 'string')
    ? value
    : null;
}

export function readSnapshot(userId: string): CoverageSnapshot | null {
  try {
    const raw = storage()?.getItem(keyOf(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Record<string, unknown>> | null;
    const themselves = ids(parsed?.themselves);
    const relink = ids(parsed?.relink);
    if (typeof parsed?.at !== 'string' || !themselves || !relink) return null;
    return { at: parsed.at, themselves, relink };
  } catch {
    return null;
  }
}

export function writeSnapshot(userId: string, snapshot: CoverageSnapshot) {
  try {
    storage()?.setItem(keyOf(userId), JSON.stringify(snapshot));
  } catch {
    // Storage refused: next time is a first visit.
  }
}

/** The snapshot of now, to keep when the page is left. */
export function snapshotOf(
  rows: readonly PersonLinksView[],
  inUse: readonly string[],
  now: Date,
): CoverageSnapshot {
  const themselves: string[] = [];
  const relink: string[] = [];
  for (const row of rows) {
    const bucket = coverageBucket(row, inUse, now);
    if (bucket === 'themselves') themselves.push(row.person.id);
    if (bucket === 'relink') relink.push(row.person.id);
  }
  return { at: now.toISOString(), themselves, relink };
}

export interface SinceVisit {
  /** People who push as themselves now and did not then. */
  joined: number;
  /** People whose link broke since. */
  broke: number;
}

/**
 * What changed since the snapshot, or null when there is nothing to say: a
 * first visit, back too soon, or no change.
 */
export function sinceVisit(
  previous: CoverageSnapshot | null,
  current: CoverageSnapshot,
  openedAt: number,
): SinceVisit | null {
  if (!previous) return null;
  const left = Date.parse(previous.at);
  if (!Number.isFinite(left) || openedAt - left < AWAY_MS) return null;
  const before = new Set(previous.themselves);
  const brokenBefore = new Set(previous.relink);
  const joined = current.themselves.filter((id) => !before.has(id)).length;
  const broke = current.relink.filter((id) => !brokenBefore.has(id)).length;
  return joined + broke > 0 ? { joined, broke } : null;
}
